import React, { useState, useEffect } from 'react';
import { X, Building2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';

/**
 * CompanyConfirmationModal
 * Modal to confirm that the manufacturing company has confirmed the order
 * before proceeding to payment and loading stages
 */
const CompanyConfirmationModal = ({ isOpen, onClose, onConfirm, order, loading }) => {
  const [confirmed, setConfirmed] = useState(false);

  // Reset checkbox when modal opens
  useEffect(() => {
    if (isOpen) {
      setConfirmed(false);
    }
  }, [isOpen]);

  // ESC key handler to close modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, loading]);

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!confirmed) {
      toast.info('Please confirm that the company has accepted this order');
      return;
    }

    onConfirm();
  };

  if (!isOpen || !order) return null;

  const companyName = order.manufacturer_name || order.company_name || 'Manufacturing Company';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-purple-600" />
              Company Confirmation
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Order #{order.order_number}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* Company Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Manufacturing Company</p>
            <p className="text-lg font-semibold text-gray-900">{companyName}</p>
          </div>

          {/* Confirmation Message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Important: Company Confirmation Required
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Before proceeding to the loading stage, please confirm that {companyName} has:
              </p>
              <ul className="list-disc list-inside text-sm text-blue-700 mt-2 space-y-1">
                <li>Reviewed and accepted the order details</li>
                <li>Confirmed availability of products</li>
                <li>Agreed to the delivery timeline</li>
              </ul>
            </div>
          </div>

          {/* Confirmation Checkbox */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="company-confirmed-checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              disabled={loading}
              className="mt-1 w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 disabled:opacity-50"
            />
            <label
              htmlFor="company-confirmed-checkbox"
              className="text-sm text-gray-700 cursor-pointer select-none"
            >
              I confirm that <strong>{companyName}</strong> has reviewed and accepted this order,
              and is ready to proceed with fulfillment.
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !confirmed}
              className="flex-1 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Confirming...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Confirm & Continue</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyConfirmationModal;
